import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import * as Icons from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import PortalStrategicReview from "@/pages/portal/PortalStrategicReview";

/** Staff Client-Preview Mode — renders the EXACT client portal experience (read-only). */
export default function StrategicReviewPreview() {
  const { clientId, aid } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/strategic-review/assignments/${aid}/portal-preview`);
        setData(data);
      } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Could not load preview"); }
      finally { setLoading(false); }
    })();
  }, [aid]);

  const backToAssembly = () => navigate(`/client-success/strategic-review/${clientId}`);
  const goPublish = () => navigate(`/client-success/strategic-review/${clientId}?publish=${aid}`);

  return (
    <div className="min-h-screen bg-slate-50" data-testid="sr-client-preview">
      {/* Preview banner (interactive — outside the read-only wrapper) */}
      <div className="sticky top-0 z-30 bg-amber-500 text-white" data-testid="sr-preview-banner">
        <div className="max-w-6xl mx-auto px-4 py-3 flex flex-col sm:flex-row sm:items-center gap-3">
          <div className="flex items-center gap-2 flex-1">
            <Icons.Eye className="h-5 w-5" />
            <div>
              <div className="font-semibold text-sm">Client Preview Mode</div>
              <div className="text-[12px] text-amber-50">This shows exactly what the client will see after publication. Preview is read-only — no edits are saved.</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={backToAssembly} className="bg-white/15 hover:bg-white/25 px-3 py-1.5 rounded-lg text-sm" data-testid="sr-preview-exit">Exit Preview</button>
            <button onClick={backToAssembly} className="bg-white/15 hover:bg-white/25 px-3 py-1.5 rounded-lg text-sm" data-testid="sr-preview-return">Return to Assembly</button>
            <button onClick={goPublish} className="bg-slate-900 hover:bg-slate-800 px-3 py-1.5 rounded-lg text-sm font-medium inline-flex items-center gap-1" data-testid="sr-preview-publish">
              <Icons.Send className="h-4 w-4" />Publish &amp; Send
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-6">
        {loading ? (
          <div className="text-center py-20 text-slate-400">Loading client preview…</div>
        ) : !data ? (
          <div className="text-center py-20 text-slate-400">Preview unavailable.</div>
        ) : (
          <div className="pointer-events-none select-none" data-testid="sr-preview-body" aria-hidden="true">
            <PortalStrategicReview preview={data} />
          </div>
        )}
      </div>
    </div>
  );
}
